import { Link, PrimaryButton } from '../../../../widgets/buttons';
import { Checkbox } from '../../../../widgets/checkbox/checkbox';
import { Dialog, DialogTitle, DialogWindow } from '../../../../widgets/dialog';
import { FormControl } from '../../../../widgets/formControl';
import { Input } from '../../../../widgets/input';
import { NotificationBanner } from '../../../../widgets/notificationBanner/notificationBanner';
import { removeNotification, showInfoNotification, showNotification } from '../../../../widgets/notificationToaster';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-relay';
import { backofficeUrl, T } from 'src/shared/helpers';
import styled from 'styled-components';
import { providerQueryAddMutation } from '../__generated__/providerQueryAddMutation.graphql';
import { ProductProviders } from '../providerElements';
import { addQuery } from '../providerQuery';
import { clearWizardProgress, loadWizardProgress, saveWizardProgress } from '../wizardProgress';

const CUSTOMCAT_GUIDE_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/360010238099-How-to-connect-and-use-CustomCat-with-Zakeke';

const StyledDialogWindow = styled(DialogWindow)`
    max-width: 700px;
    height: calc(90vh - 20px);
    padding-left: 24px !important;
    padding-right: 24px !important;
    display: flex;
    flex-direction: column;

    > div {
        overflow: visible;
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
    }
`;

const DialogCaption = styled.p`
    font-size: 13px;
    line-height: 18px;
    color: #6b6b6b;
    margin: -16px 0 20px;
`;

const WizardContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const StepIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 32px;
`;

const StepDot = styled.div<{ $active: boolean; $completed: boolean; $clickable: boolean }>`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => (props.$active || props.$completed ? '#fff' : '#9e9e9e')};
    background: ${(props) => (props.$completed ? '#2e7d32' : props.$active ? props.theme.app.primary : '#e0e0e0')};
    transition: all 0.2s ease;
    cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};

    &:hover {
        opacity: ${(props) => (props.$clickable ? 0.8 : 1)};
    }
`;

const StepLine = styled.div<{ $completed: boolean }>`
    width: 40px;
    height: 2px;
    background: ${(props) => (props.$completed ? '#2e7d32' : '#e0e0e0')};
    transition: all 0.2s ease;
`;

const StepContentArea = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
`;

const StepTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px;
`;

const StepBody = styled.div`
    font-size: 13px;
    line-height: 18px;
    color: #333;
    display: flex;
    flex-direction: column;
    gap: 8px;

    p {
        margin: 4px 0;
    }

    ul {
        margin: 4px 0;
    }

    ${NotificationBanner} {
        margin: 4px 0;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    gap: 8px;
    padding-top: 8px;
`;

const IntegrateContainer = styled.div`
    padding: 32px;
    display: flex;
    justify-content: center;
`;

const GuideLinkSpacer = styled.div`
    flex: 1;
    min-height: 20px;
`;

const GuideLink = styled.div`
    padding: 12px 0 0;
    border-top: 1px solid #dbe2e6;
`;

const SuccessMessage = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #2e7d32;
    text-align: center;
    padding: 12px;
`;

const StepIndicatorBar: FC<{
    totalSteps: number;
    currentStep: number;
    completedSteps: boolean[];
    onStepClick?: (step: number) => void;
}> = ({ totalSteps, currentStep, completedSteps, onStepClick }) => {
    return (
        <StepIndicator>
            {Array.from({ length: totalSteps }, (_, i) => {
                const canClick = completedSteps[i] && i < currentStep;
                return (
                    <React.Fragment key={i}>
                        {i > 0 && <StepLine $completed={completedSteps[i - 1]} />}
                        <StepDot $active={currentStep === i} $completed={completedSteps[i]} $clickable={canClick} onClick={() => canClick && onStepClick?.(i)}>
                            {completedSteps[i] ? '✓' : i + 1}
                        </StepDot>
                    </React.Fragment>
                );
            })}
        </StepIndicator>
    );
};

interface CustomCatIntegrationWizardProps {
    onAuthorized: () => void;
}

const CustomCatIntegrationWizard: FC<CustomCatIntegrationWizardProps> = ({ onAuthorized }) => {
    const savedProgress = useRef(loadWizardProgress(ProductProviders.CustomCat));
    const [currentStep, setCurrentStep] = useState(savedProgress.current?.step ?? 0);
    const [step1Confirmed, setStep1Confirmed] = useState(savedProgress.current?.confirmations[0] ?? false);
    const [step2Confirmed, setStep2Confirmed] = useState(savedProgress.current?.confirmations[1] ?? false);
    const [secretKey, setSecretKey] = useState('');
    const [saved, setSaved] = useState(false);
    const [addApiKey] = useMutation<providerQueryAddMutation>(addQuery);

    useEffect(() => {
        if (saved) return;
        saveWizardProgress(ProductProviders.CustomCat, currentStep, [step1Confirmed, step2Confirmed, saved]);
    }, [currentStep, step1Confirmed, step2Confirmed, saved]);

    const handleStep1Toggle = (checked: boolean) => {
        if (checked) {
            setStep1Confirmed(true);
            setCurrentStep(1);
        } else {
            setStep1Confirmed(false);
            setStep2Confirmed(false);
            setCurrentStep(0);
        }
    };

    const handleStep2Toggle = (checked: boolean) => {
        if (checked) {
            setStep2Confirmed(true);
            setCurrentStep(2);
        } else {
            setStep2Confirmed(false);
            setCurrentStep(1);
        }
    };

    const handleStepClick = (step: number) => {
        if (step === 0) {
            setStep1Confirmed(false);
            setStep2Confirmed(false);
        } else if (step === 1) {
            setStep2Confirmed(false);
        }
        setCurrentStep(step);
    };

    const handleSave = () => {
        const notification = showInfoNotification({
            message: T._(`Creating Api Key... please don't exit from page.`, 'Admin'),
            dismiss: undefined
        });
        addApiKey({
            variables: {
                input: {
                    providerId: ProductProviders.CustomCat,
                    secretKey: secretKey,
                    descriptionKey: 'CustomCat key',
                    autoConfirmOrders: false,
                    printfulPrefix: null,
                    printfulSuffix: null,
                    invitionPhoneNumber: null
                }
            },
            onCompleted: () => {
                removeNotification(notification);
                showNotification({ type: 'success', message: T._('Api Key created.', 'Admin'), dismiss: { duration: 5000 } });
                setSaved(true);
                clearWizardProgress(ProductProviders.CustomCat);
                onAuthorized();
                window.location.href = backofficeUrl('/integration/provider-configuration-page/customcat');
            },
            onError: () => {
                removeNotification(notification);
                showNotification({ type: 'danger', message: T._('Error on creating', 'Admin'), dismiss: { duration: 5000 } });
            },
            updater: (store) => {
                const root = store.getRoot();
                const addedPOD = store.getRootField('addApiKey');
                const oldPOD = (root.getLinkedRecords('apiKey', {}) as []) ?? [];
                const allPOD = [...oldPOD, addedPOD];
                root.setLinkedRecords(allPOD, 'apiKey', {});
            }
        });
    };

    const completedSteps = [step1Confirmed, step2Confirmed, saved];

    return (
        <WizardContainer>
            <NotificationBanner
                type={'INFO'}
                variant={'slim'}
                content={
                    <>
                        {T._('We recommend following along with the', 'Admin')}{' '}
                        <Link target='_blank' href={CUSTOMCAT_GUIDE_URL}>
                            {T._('full integration guide', 'Admin')}
                        </Link>
                        {T._(', which includes screenshots for each step.', 'Admin')}
                    </>
                }
            />

            <StepIndicatorBar totalSteps={3} currentStep={currentStep} completedSteps={completedSteps} onStepClick={handleStepClick} />

            <StepContentArea>
                {currentStep === 0 && (
                    <StepBody>
                        <StepTitle>{T._('Create an API store on CustomCat', 'Admin')}</StepTitle>
                        <p>
                            {T._(
                                'Go to the CustomCat website and sign up or log in. In your CustomCat back-office, go to My Stores and click on Create API Order under the API icon.',
                                'Admin'
                            )}
                        </p>
                        <p>{T._('Enter the Store Name and Store URL, then click Connect.', 'Admin')}</p>
                        <NotificationBanner
                            type={'WARNING'}
                            variant={'slim'}
                            content={T._(
                                'Make sure to choose API even if your store is on Shopify or WooCommerce platforms, otherwise the connection will not work.',
                                'Admin'
                            )}
                        />
                        <Checkbox checked={step1Confirmed} onChange={handleStep1Toggle} label={T._('I have created the API store on CustomCat', 'Admin')} />
                    </StepBody>
                )}

                {currentStep === 1 && (
                    <StepBody>
                        <StepTitle>{T._('Copy your API key from CustomCat', 'Admin')}</StepTitle>
                        <p>{T._('In your CustomCat store, click on API in the top right corner and copy the API key.', 'Admin')}</p>
                        <NotificationBanner
                            type={'WARNING'}
                            variant={'slim'}
                            content={T._(
                                'You will see two API keys. Make sure to copy the one with Read/Write access level, otherwise the connection will not work.',
                                'Admin'
                            )}
                        />
                        <Checkbox
                            checked={step2Confirmed}
                            onChange={handleStep2Toggle}
                            label={T._('I have copied the API key with Read/Write access', 'Admin')}
                        />
                    </StepBody>
                )}

                {currentStep === 2 && (
                    <StepBody>
                        <StepTitle>{T._('Paste your API key', 'Admin')}</StepTitle>
                        <p>{T._('Paste the API key you copied from CustomCat.', 'Admin')}</p>
                        {!saved ? (
                            <>
                                <FormControl label={T._('Api Key', 'Admin')}>
                                    <Input
                                        type='string'
                                        value={secretKey}
                                        onChange={(e) => setSecretKey(e.currentTarget.value)}
                                        placeholder={T._('Paste your Api key here...', 'Admin')}
                                    />
                                </FormControl>
                                <ButtonContainer>
                                    <PrimaryButton size='small' onClick={handleSave} disabled={!secretKey.trim()}>
                                        {T._('Save', 'Admin')}
                                    </PrimaryButton>
                                </ButtonContainer>
                            </>
                        ) : (
                            <SuccessMessage>{T._('CustomCat integration is complete! You are all set.', 'Admin')}</SuccessMessage>
                        )}
                    </StepBody>
                )}
            </StepContentArea>

            <GuideLinkSpacer />

            <GuideLink>
                <Link target='_blank' href={CUSTOMCAT_GUIDE_URL}>
                    {T._('Need help? Read the full integration guide', 'Admin')}
                </Link>
            </GuideLink>
        </WizardContainer>
    );
};

export const CustomCatIntegrationDialog: FC<{ onClose: () => void; onIntegrated: () => void }> = ({ onClose, onIntegrated }) => {
    return (
        <Dialog windowDecorator={StyledDialogWindow} onClose={onClose}>
            <DialogTitle>
                {T._('Connect', 'Admin')} CustomCat {T._('to your Zakeke account', 'Admin')}
            </DialogTitle>
            <DialogCaption>{T._('Sell CustomCat products as customizable on your store with automatic fulfillment and drop-shipping.', 'Admin')}</DialogCaption>
            <CustomCatIntegrationWizard onAuthorized={onIntegrated} />
        </Dialog>
    );
};

export const CustomCatIntegrateButton: FC<{ onIntegrated: () => void }> = ({ onIntegrated }) => {
    const [showDialog, setShowDialog] = useState(false);

    return (
        <>
            <IntegrateContainer>
                <PrimaryButton size='small' onClick={() => setShowDialog(true)}>
                    {T._('Integrate', 'Admin')}
                </PrimaryButton>
            </IntegrateContainer>
            {showDialog && <CustomCatIntegrationDialog onClose={() => setShowDialog(false)} onIntegrated={onIntegrated} />}
        </>
    );
};

export default CustomCatIntegrationWizard;
